/** Wizard state — the LIVE subset of the TourOperatorCreateInput backend
 * serializer (identity + admin + languages + the onboarding profile). The
 * retired dialog steps' fields (branchio keys, chat flags, RBAC lists, auth
 * methods) are no longer collected or sent — the serializer defaults them, and
 * their real homes are the preflight items. */
import type { OutboundMode, SandboxAllowlist } from '@/api/sandbox';
import { defaultLang } from '@/utils/defaultLang';

export interface AuthMethodInput {
  name: string;
  domain: string;
  client_id: string;
  allow_signup: boolean;
  allowed_domains: string[];
  access_group_codes: string[];
}

export type Restech =
  | 'none'
  | 'fareharbor'
  | 'ventrata'
  | 'bokun'
  | 'rezdy'
  | 'trekksoft'
  | 'xml'
  | 'other';

export type Cadence = 'day' | 'multi_day';

export type AppStoreAccount = 'curated_planet' | 'operator';

/** Chat/notification channels offered to guests. */
export type Channel = 'sms' | 'whatsapp' | 'push';

/** A colleague who also needs a dashboard account. Stored verbatim at
 *  `config.onboarding.team_members`; the accounts are created later, by the
 *  TEAM_USERS preflight item. */
export interface TeamMemberInput {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
}

export interface WizardData {
  // Step 1
  name: string;
  code: string;
  email: string;
  phone: string;
  url: string;
  /** Tour_Operator.payment_url — filled on 11–13 of 13 live tenants, so the Basics step asks. */
  payment_url: string;
  /** Tour_Operator.external_payment_processing — the operator processes payments on its own. */
  external_payment_processing: boolean;
  // Step 2
  admin_email: string;
  admin_password: string;
  admin_first_name: string;
  admin_last_name: string;
  admin_phone: string;
  /** The rest of the team — optional, and the only REQUIRED preflight input the
   *  wizard used to leave for ops to supply again on another screen. */
  team_members: TeamMemberInput[];
  // Languages (config.i18n)
  lang_enabled: string[];
  lang_fallback: string[];
  // Profile — has_app is the Tour_Operator.has_app column (never config).
  has_app: boolean;
  /** Reviews on (D39; default on) — selects the reviews pack, REVIEW_CONFIG and the survey cron gate. */
  reviews_enabled: boolean;
  /** Operator inbox for the negative-review alert; '' = none. */
  reviews_alert_email: string;
  // Profile step — drives `config.onboarding` on the backend.
  restech: Restech;
  channels: Channel[];
  cadence: Cadence;
  guides_split_chat: boolean;
  vehicle_tracking: boolean;
  own_corporate_email: boolean;
  app_store_account: AppStoreAccount;
  has_existing_apps: boolean;
  /** Tag_Category_Set id, or null when none chosen. */
  tag_category_set: number | null;
  /** Agroup_Template_Group id, or null when none chosen. */
  ag_template_group: number | null;
  // Sandbox (SBX D8) — "Create a sandbox too": `<code>sbx`, in the same transaction.
  create_sandbox: boolean;
  /** The sandbox admin, a separate account (O3); '' = the admin e-mail above (same password). */
  sandbox_admin_email: string;
  sandbox_outbound_mode: OutboundMode;
  sandbox_allowlist: Required<SandboxAllowlist>;
}

/** The wizard's sandbox answers — sent only with `create_sandbox`. */
export const SANDBOX_WIZARD_KEYS = [
  'create_sandbox',
  'sandbox_admin_email',
  'sandbox_outbound_mode',
  'sandbox_allowlist',
] as const satisfies readonly (keyof WizardData)[];

export const WIZARD_DEFAULTS: WizardData = {
  name: '',
  code: '',
  email: '',
  phone: '',
  url: '',
  payment_url: '',
  external_payment_processing: false,
  admin_email: '',
  admin_password: '',
  admin_first_name: '',
  admin_last_name: '',
  admin_phone: '',
  team_members: [],
  lang_enabled: ['en'],
  lang_fallback: ['en'],
  has_app: true,
  reviews_enabled: true,
  reviews_alert_email: '',
  // Profile
  restech: 'none',
  channels: [],
  cadence: 'day',
  guides_split_chat: false,
  vehicle_tracking: false,
  own_corporate_email: false,
  app_store_account: 'curated_planet',
  has_existing_apps: false,
  tag_category_set: null,
  ag_template_group: null,
  create_sandbox: false,
  sandbox_admin_email: '',
  sandbox_outbound_mode: 'outbox',
  sandbox_allowlist: { domains: [], emails: [], phones: [] },
};

/** The create payload: the sandbox answers only when the sandbox is created too. */
export function createPayload(data: WizardData): Partial<WizardData> {
  if (data.create_sandbox) return data;
  const out: Partial<WizardData> = { ...data };
  for (const key of SANDBOX_WIZARD_KEYS) delete out[key];
  return out;
}

/** The create wizard's starting data: the defaults, then the easy flow's prefill, with the
 *  fallback set from the default language — the last enabled one (AIE D21). */
export function initialWizardData(prefill: Partial<WizardData> | null): WizardData {
  const d = { ...WIZARD_DEFAULTS, ...prefill };
  return { ...d, lang_fallback: [defaultLang(d.lang_enabled, 'en')] };
}
